import Link from "next/link";
import { VotoSintetico } from "@/app/components/Stelle";
import {
  formattaPrezzo,
  localized,
  mediaUrl,
  nomePaese,
  type Price,
  type ProductHit,
} from "@/lib/api";

/**
 * La scheda di un prodotto: immagine sopra, il minimo indispensabile sotto.
 *
 * Il minimo indispensabile è quello che serve a decidere se aprirla, e sono
 * quattro cose: **cosa è, quanto costa, quanti minimo, in quanto tempo**. Più
 * il paese di produzione, che è un criterio d'acquisto reale.
 *
 * Fuori restano materiali, lavorazioni, certificazioni e descrizione. Non
 * perché non contino — contano, nella scheda intera — ma perché in una griglia
 * di dodici riquadri diventano rumore: nessuno le legge, e allungano ogni
 * scheda facendo entrare meno prodotti nello schermo.
 *
 * Non mostra chi produce: nel modello di rivendita il compratore acquista da
 * noi, e vedere il fornitore gli darebbe il modo di scavalcarci alla seconda
 * commessa. Resta il paese, che non identifica nessuno.
 *
 * Restituisce un `<article>` e non un `<li>`: chi la usa la mette in una lista,
 * in una griglia o in una fila che scorre, e sa lui che contenitore serve.
 */

/** Il lotto minimo è la prima cosa che un compratore guarda. */
function lotto(minimo: number | null, unita: string | null) {
  const pezzi = unita ?? "pezzi";
  if (minimo === null) return { testo: "lotto su richiesta", singolo: false };
  if (minimo <= 1) return { testo: `al ${unita ?? "pezzo"}`, singolo: true };
  return { testo: `min ${minimo} ${pezzi}`, singolo: false };
}

export function ResultCard({ hit, price }: { hit: ProductHit; price?: Price | null }) {
  const immagine = mediaUrl(hit.image_uri);
  const { testo: testoLotto, singolo } = lotto(hit.min_order_quantity, hit.unit);
  const prezzo = formattaPrezzo(price ?? null);

  return (
    <article className="h-full overflow-hidden rounded-xl border border-line bg-surface transition hover:border-accent">
      <Link href={`/products/${hit.product_id}`} className="flex h-full flex-col">
        {/* L'immagine sopra, a proporzione fissa: schede di altezza diversa in
            una griglia fanno un bordo a denti di sega. */}
        <div className="aspect-[4/3] w-full overflow-hidden border-b border-line bg-surface-strong">
          {immagine ? (
            // eslint-disable-next-line @next/next/no-img-element -- SVG servito dall'API
            <img
              src={immagine}
              alt={localized(hit.product_name)}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-xs text-on-strong-muted">
              nessuna immagine
            </div>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-1 p-3">
          <h3 className="line-clamp-2 text-sm font-medium leading-snug">
            {localized(hit.product_name)}
          </h3>

          {/* Il voto subito sotto il nome, prima del prezzo: è il filtro con cui
              si scarta una scheda, e messo dopo il prezzo lo si leggerebbe
              quando la decisione è già presa. Non compare finché non c'è. */}
          {hit.rating && hit.rating.reviews_count > 0 && (
            <VotoSintetico voto={hit.rating} />
          )}

          <p className="mt-auto pt-1">
            {prezzo ? (
              <>
                <span className="text-lg font-semibold">{prezzo}</span>{" "}
                <span className="text-xs text-muted">
                  {price?.show_gross ? "IVA inclusa" : "+ IVA e spedizione"}
                  {hit.min_order_quantity && hit.min_order_quantity > 1 ? " · a pezzo" : ""}
                </span>
              </>
            ) : (
              <span className="text-sm text-muted">Prezzo su richiesta</span>
            )}
          </p>

          <p className="text-xs">
            <span className={singolo ? "font-medium text-accent" : "text-muted"}>
              {testoLotto}
            </span>
            {hit.lead_time_days !== null && (
              <span className="text-muted"> · {hit.lead_time_days} giorni</span>
            )}
          </p>

          <p className="text-xs text-muted">Prodotto in {nomePaese(hit.country_code)}</p>
        </div>
      </Link>
    </article>
  );
}
